<template>
  <ConfirmDialog
    :open="open"
    max-height="700"
    class-name="open-key-creation-dialog"
    :disabled-confirm-action="!!key"
    :click-outside-to-close="false"
    @cancel="emit('cancel')"
    @confirm="createKey"
  >
    <template #title>Create new API key</template>
    <template #detail> Configure your integrations' scopes and settings</template>

    <template #body>
      <div class="create-key">
        <TextInput
          v-model="name"
          placeholder="Give your key unique name"
          :disabled="!!key"
          label="Key name"
          :error="errors.name"
          required
          @enter="createKey"
        />
        <div class="create-key__permissions__list-wrapper">
          <div
            v-for="(group, index) in PERMISSION_GROUPS"
            :key="group.id"
            class="create-key__permissions__list"
          >
            <PermissionGroupSelector
              :group="group"
              @change="setSelectedAbilities"
            />

            <Divider v-if="index !== PERMISSION_GROUPS.length - 1" />
          </div>
        </div>

        <div
          v-if="errors.permissions"
          class="create-key__permissions__error"
        >
          {{ errors.permissions }}
        </div>

        <CodeBlock
          v-if="key"
          :command="key"
          label="Your access key:"
          @copy:success="onSuccessfulCopy"
          @copy:failure="onFailedCopy"
        />
        <p
          v-if="key"
          class="key__notice"
        >
          Please write down your key as it cannot be retrieved in the future.
        </p>
      </div>
    </template>
  </ConfirmDialog>
</template>

<script lang="ts">
export default {
  name: 'CreateKeyDialog',
}
</script>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import type { TeamPayload } from '@/store/types/TeamPayload'
import CodeBlock from '@/uiKit/CodeBlock/CodeBlock.vue'
import { useCodeBlock } from '@/uiKit/CodeBlock/useCodeBlock'

import { useToast } from '@/uiKit/Toast/useToast'

import ConfirmDialog from '@/uiKit/Dialog/ConfirmDialog.vue'
import TextInput from '@/uiKit/TextInput/TextInput.vue'
import Divider from '@/uiKit/Divider/Divider.vue'
import { createApiKey } from '@/backend/darwin/createApiKey'

import PermissionGroupSelector from './PermissionGroupSelector.vue'
import { GROUPED_ABILITIES, PERMISSION_GROUPS } from './data'
import type { AbilityGroup, AbilityID } from './types'
import { useApiKeyStore } from './useApiKeyStore'

type KeyValidationErrors = { name?: string; permissions?: string }
const { onSuccessfulCopy, onFailedCopy } = useCodeBlock()

const props = defineProps<{ open: boolean; team: TeamPayload }>()

const emit = defineEmits<{
  (e: 'cancel'): void
}>()

const key = ref<null | string>(null)
const name = ref('')

const errors = ref<KeyValidationErrors>({})

const selectedAbilities = ref<AbilityID[]>([])
const permissions = computed<[AbilityID, string][]>(() =>
  selectedAbilities.value.map((a) => [a, 'all']),
)

const setSelectedAbilities = (groupId: AbilityGroup, abilities: AbilityID[]): void => {
  const groupAbilities = GROUPED_ABILITIES[groupId]

  selectedAbilities.value = selectedAbilities.value
    .filter((a) => !groupAbilities.includes(a))
    .concat(abilities)
}

const validate = (): boolean => {
  errors.value = {}

  if (!name.value || name.value === '') {
    errors.value.name = 'You must type in a name.'
  }

  if (permissions.value.length === 0) {
    errors.value.permissions = 'You must select at least one ability'
  }

  return Object.keys(errors.value).length === 0
}

const toast = useToast()
const keyStore = useApiKeyStore()

const createKey = async (): Promise<void> => {
  if (!validate()) {
    return
  }

  const result = await createApiKey({
    name: name.value,
    permissions: permissions.value,
    teamId: props.team.id,
  })

  if (result.ok) {
    key.value = `${result.data.prefix}.${result.data.value}`
    keyStore.pushKey(result.data)
  } else {
    toast.warning({ meta: { title: result.error.message as string } })
  }
}
</script>

<style lang="scss" scoped>
.create-key {
  padding: 16px;
  @include col;
}

.create-key__permissions__list-wrapper {
  margin-bottom: 16px;
}

.create-key__permissions__list {
  @include col;
}

.key__notice {
  @include typographyRegularBody200;
  color: var(--color-text-subtle);
  margin-top: 4px;
}

.create-key__permissions__error {
  @include typographyRegularBody200;
  color: var(--color-text-critical);
}
</style>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="scss">
/*  This `DarwinDialog` modal is normally called from `<modal>` and it needs to be on top of it.
    There is no current support to control z-index, so the below line is a workaround.
    `.open-key-creation-dialog` is added to the dialog so we make sure the leaked css isn't
    changing the `z-index` of other dialogs.
*/
.darwin-dialog.open-key-creation-dialog {
  z-index: var(--z-index-modal-level-2-overlay) !important;
}
</style>
